import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { memberApi } from "../lib/memberApi";
import { UploadCloud, Loader2, FileDown, ShieldAlert, CheckCircle2 } from "lucide-react";

const DISCLOSURE_TEXT = "PHMEG provides this system as a drafting, scoring, and decision-support aid only. We make no promises or claims that using this system will guarantee eligibility, responsiveness, shortlisting, funding, contract award, or any other procurement outcome.";

const initialContext = {
  nofo: {
    opportunity_number: "",
    agency: "",
    eligibility: "",
    application_requirements: "",
    review_criteria: "",
    deadline: "",
    program_purpose: "",
  },
  organizational_profile: {
    Organization_Name: "",
    Mission: "",
    Capacity: "",
  },
  program_design: {
    Program_Name: "",
    Target_Population: "",
    Objectives: "",
    Activities: "",
    Logic_Model: "",
  },
  implementation_plan: {
    Activities: "",
    Timeline: "",
    Milestones: "",
    Operational_Steps: "",
  },
  staffing_plan: {
    Key_Personnel: "",
    FTE_Allocations: "",
    Staffing_Justification: "",
  },
  budget_narrative: {
    Budget_Total: "",
    Personnel_Costs: "",
    Cost_Justification: "",
  },
  evaluation_plan: {
    Outcomes: "",
    Indicators: "",
    Data_Collection_Methods: "",
    Evaluation_Design: "",
  },
  sustainability_plan: {
    Sustainability_Strategy: "",
    Funding_Plan: "",
    Partnerships: "",
  },
};

const fields = [
  ["nofo", "opportunity_number", "Opportunity number / solicitation ID"],
  ["nofo", "agency", "Issuing agency / department"],
  ["nofo", "eligibility", "Eligibility / vendor qualifications"],
  ["nofo", "application_requirements", "Required attachments / forms"],
  ["nofo", "review_criteria", "Review criteria / evaluation factors"],
  ["nofo", "deadline", "Deadline or due date"],
  ["nofo", "program_purpose", "Program purpose / scope summary", true],
  ["organizational_profile", "Organization_Name", "Organization name"],
  ["organizational_profile", "Mission", "Mission statement", true],
  ["organizational_profile", "Capacity", "Organizational capacity / past performance", true],
  ["program_design", "Program_Name", "Project / proposal title"],
  ["program_design", "Target_Population", "Target population / users / service area", true],
  ["program_design", "Objectives", "Objectives / deliverables", true],
  ["program_design", "Activities", "Activities / technical approach", true],
  ["program_design", "Logic_Model", "Logic model / solution framework", true],
  ["implementation_plan", "Timeline", "Timeline / phase plan", true],
  ["implementation_plan", "Milestones", "Key milestones", true],
  ["implementation_plan", "Operational_Steps", "Operational steps", true],
  ["staffing_plan", "Key_Personnel", "Key personnel / team roles", true],
  ["staffing_plan", "FTE_Allocations", "Labor allocation / level of effort"],
  ["staffing_plan", "Staffing_Justification", "Staffing justification", true],
  ["budget_narrative", "Budget_Total", "Budget total"],
  ["budget_narrative", "Personnel_Costs", "Personnel / pricing inputs", true],
  ["budget_narrative", "Cost_Justification", "Budget narrative starter", true],
  ["evaluation_plan", "Outcomes", "Outcomes / expected results", true],
  ["evaluation_plan", "Indicators", "Indicators / KPIs", true],
  ["evaluation_plan", "Data_Collection_Methods", "Data collection / reporting methods", true],
  ["evaluation_plan", "Evaluation_Design", "Evaluation / QA approach", true],
  ["sustainability_plan", "Sustainability_Strategy", "Sustainability / continuation approach", true],
  ["sustainability_plan", "Funding_Plan", "Future funding / renewal plan", true],
  ["sustainability_plan", "Partnerships", "Partners / subcontractors / collaborators", true],
];

export default function GrantAutomation({ apiClient = api, memberMode = false, onDocumentCreated = null }) {
  const [context, setContext] = useState(initialContext);
  const [procurementType, setProcurementType] = useState("grant");
  const [solicitationLevel, setSolicitationLevel] = useState("federal");
  const [acceptedDisclosure, setAcceptedDisclosure] = useState(false);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [packageResult, setPackageResult] = useState(null);

  const client = memberMode ? memberApi : apiClient;

  const setFieldValue = (section, key, value) => {
    setContext((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;
    if (!uploadedFile.name.toLowerCase().endsWith(".pdf")) {
      setError("Please select a PDF document.");
      return;
    }

    setFile(uploadedFile);
    setParsing(true);
    setError("");
    setSuccessMsg("");
    const formData = new FormData();
    formData.append("file", uploadedFile);

    try {
      const { data } = await client.post(memberMode ? "/member/grant/parse-nofo" : "/grant/parse-nofo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContext((prev) => ({
        ...prev,
        nofo: {
          ...prev.nofo,
          opportunity_number: data.opportunity_number || prev.nofo.opportunity_number,
          agency: data.agency || prev.nofo.agency,
          eligibility: data.eligibility || prev.nofo.eligibility,
          application_requirements: data.application_requirements || prev.nofo.application_requirements,
          review_criteria: data.review_criteria || prev.nofo.review_criteria,
          deadline: data.deadline || prev.nofo.deadline,
          program_purpose: data.program_purpose || prev.nofo.program_purpose,
        },
      }));
      setSuccessMsg("Solicitation parsed successfully. Review the fields below before generating.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not parse the solicitation PDF.");
    } finally {
      setParsing(false);
    }
  };

  const downloadBlob = async (documentId, filename, mimeType) => {
    const response = await memberApi.get(`/member/documents/${documentId}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!acceptedDisclosure) {
      setError("You must accept the disclosure and release statement before generation.");
      return;
    }

    setGenerating(true);
    setError("");
    setSuccessMsg("");
    setPackageResult(null);

    try {
      if (memberMode) {
        const { data } = await memberApi.post("/member/grant/generate", {
          ...context,
          procurement_type: procurementType,
          solicitation_level: solicitationLevel,
          disclosure_accepted: acceptedDisclosure,
        });
        setPackageResult(data);
        setSuccessMsg("Your almost-ready submission package has been stored in the member portal.");
        if (onDocumentCreated && data.document) onDocumentCreated(data.document);
      } else {
        const response = await apiClient.post("/grant/generate", {
          ...context,
          procurement_type: procurementType,
          solicitation_level: solicitationLevel,
          disclosure_accepted: acceptedDisclosure,
        }, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `PHMEG-Almost-Ready-Submission-${context.nofo.opportunity_number || "Draft"}.docx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setSuccessMsg("Almost-ready submission package generated successfully.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate the submission package.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="grant-automation-page">
      <div className="py-12 border-b border-[var(--border)] mb-10">
        <div className="overline text-[var(--red)] mb-4">{memberMode ? "Member Tool" : "Internal Tool"}</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl leading-[1.05]">
          Almost-ready <span className="text-[var(--red)]">grant and contract</span> submission builder
        </h1>
        <p className="mt-4 text-[var(--text-2)] max-w-3xl leading-relaxed">
          Build a local, state, or federal solicitation draft with an executive summary, compliance checklist, budget narrative starter, timeline/workplan starter, and next-step guidance.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="card-dark p-6" data-testid="grant-automation-procurement-selector">
          <div className="overline mb-3">Step 1</div>
          <div className="font-display text-xl font-semibold mb-4">Choose opportunity type</div>
          <div className="flex flex-wrap gap-3">
            {[
              { id: "grant", label: "Grant" },
              { id: "contract", label: "Contract" },
            ].map((option) => (
              <button key={option.id} type="button" onClick={() => setProcurementType(option.id)} className={`px-4 py-2 border rounded-md text-sm transition ${procurementType === option.id ? "border-[var(--red)] text-white bg-[var(--surface)]" : "border-[var(--border)] text-[var(--text-2)] hover:text-white"}`} data-testid={`grant-automation-procurement-${option.id}`}>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="card-dark p-6" data-testid="grant-automation-level-selector">
          <div className="overline mb-3">Step 2</div>
          <div className="font-display text-xl font-semibold mb-4">Choose solicitation level</div>
          <div className="flex flex-wrap gap-3">
            {[
              { id: "local", label: "Local" },
              { id: "state", label: "State" },
              { id: "federal", label: "Federal" },
            ].map((option) => (
              <button key={option.id} type="button" onClick={() => setSolicitationLevel(option.id)} className={`px-4 py-2 border rounded-md text-sm transition ${solicitationLevel === option.id ? "border-[var(--red)] text-white bg-[var(--surface)]" : "border-[var(--border)] text-[var(--text-2)] hover:text-white"}`} data-testid={`grant-automation-level-${option.id}`}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="mb-10 card-dark p-8 border border-[var(--border)] relative overflow-hidden" data-testid="grant-automation-upload-box">
        <div className="max-w-xl">
          <div className="overline text-[var(--silver)] mb-3">Step 3</div>
          <h2 className="font-display text-2xl font-semibold mb-4">Upload the solicitation PDF</h2>
          <p className="text-sm text-[var(--text-2)] mb-6 leading-relaxed">
            Upload a local, state, or federal bid / NOFO / RFP PDF to prefill the opportunity details.
          </p>
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-[var(--border)] hover:border-[var(--silver)] rounded-lg p-8 cursor-pointer transition bg-black/40" data-testid="grant-automation-upload-label">
            <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" disabled={parsing} data-testid="grant-automation-file-input" />
            {parsing ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--red)]" />
                <span className="text-sm font-semibold">Analyzing solicitation PDF…</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-center">
                <UploadCloud className="w-10 h-8 text-[var(--red)] mb-1" />
                <span className="text-sm font-semibold">Upload solicitation PDF</span>
                <span className="text-xs text-[var(--text-3)]">Supported: .pdf</span>
              </div>
            )}
          </label>
          {file && !parsing && <p className="text-xs text-emerald-400 mt-3" data-testid="grant-automation-selected-file">Selected file: {file.name}</p>}
        </div>
      </section>

      {error && <div className="p-4 border border-[var(--red)] bg-red-950/20 text-[var(--red)] text-sm rounded-lg mb-6" data-testid="grant-automation-error">{error}</div>}
      {successMsg && <div className="p-4 border border-emerald-500 bg-emerald-950/10 text-emerald-400 text-sm rounded-lg mb-6 flex items-center gap-2" data-testid="grant-automation-success"><CheckCircle2 className="w-4 h-4 shrink-0" /> {successMsg}</div>}

      <form onSubmit={handleGenerate} className="space-y-8" data-testid="grant-automation-form">
        <div className="grid md:grid-cols-2 gap-4">
          {fields.map(([section, key, label, isTextArea]) => (
            <div key={`${section}-${key}`} className={isTextArea ? "md:col-span-2" : ""}>
              <label className="overline mb-2 block text-[var(--red)]">{label}</label>
              {isTextArea ? (
                <textarea value={context[section][key] || ""} onChange={(e) => setFieldValue(section, key, e.target.value)} className="input-dark" rows={4} data-testid={`grant-field-${section}-${key}`} />
              ) : (
                <input value={context[section][key] || ""} onChange={(e) => setFieldValue(section, key, e.target.value)} className="input-dark" data-testid={`grant-field-${section}-${key}`} />
              )}
            </div>
          ))}
        </div>

        <div className="card-dark p-6" data-testid="grant-automation-disclosure-block">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-[var(--red)] mt-0.5 shrink-0" />
            <div>
              <div className="overline mb-2">Disclosure & release of liability</div>
              <p className="text-sm text-[var(--text-2)] leading-relaxed" data-testid="grant-automation-disclosure-copy">{DISCLOSURE_TEXT}</p>
            </div>
          </div>
          <label className="flex items-start gap-3 mt-4 text-sm text-[var(--text-2)]" data-testid="grant-automation-disclosure-checkbox-wrapper">
            <input type="checkbox" checked={acceptedDisclosure} onChange={(e) => setAcceptedDisclosure(e.target.checked)} data-testid="grant-automation-disclosure-checkbox" />
            <span>I understand this tool does not guarantee any award outcome and requires human review before submission.</span>
          </label>
        </div>

        <button type="submit" disabled={generating} className="btn-red" data-testid="grant-automation-generate-btn">
          {generating ? (<><Loader2 className="w-4 h-4 animate-spin" /> Building package…</>) : "Generate almost-ready submission package"}
        </button>
      </form>

      {memberMode && packageResult?.document && (
        <div className="card-dark p-6 mt-8" data-testid="grant-automation-result-card">
          <div className="overline mb-2">Stored in member portal</div>
          <h3 className="font-display text-xl font-semibold mb-3">Your almost-ready draft is ready</h3>
          <p className="text-sm text-[var(--text-2)] mb-4">The package has been saved to your member documents and includes the disclosure statement, compliance checklist, attachments checklist, budget narrative starter, and timeline/workplan starter.</p>
          <button onClick={() => downloadBlob(packageResult.document.id, packageResult.document.filename, packageResult.document.mime_type)} className="btn-ghost" data-testid="grant-automation-download-result-btn">
            <FileDown className="w-4 h-4" /> Download stored draft
          </button>
        </div>
      )}
    </div>
  );
}